import { supabase } from '@/lib/supabaseClient';
import { API_URL } from '@/config/api';
import i18n from '@/i18n/i18n';

export interface Profile {
    id: string;
    firstName?: string;
    lastName?: string;
    profilePhotoUrl?: string;
    phone?: string;
    birthDate?: string;
    contactDate?: string;
    paymentDate?: string;
    price?: number;
    trainingDaysPerWeek?: number;
    preferences?: string;
    notes?: string;
    equipment?: string;
    level?: string;
}

const getAuthHeader = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return {
        'Authorization': `Bearer ${session?.access_token || ''}`
    };
};

export const profileService = {
    /**
     * Get current user's profile
     */
    getProfile: async (): Promise<{ user: any; profile: Profile | null } | null> => {
        try {
            const headers = await getAuthHeader();
            const response = await fetch(`${API_URL}/profile/me`, {
                headers
            });

            if (!response.ok) {
                console.error('Failed to fetch profile:', response.statusText);
                return null;
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching profile:', error);
            return null;
        }
    },

    /**
     * Upload avatar image
     * @param file Image file (JPG, PNG, WebP, max 5MB)
     * @returns Avatar URL and updated profile
     */
    uploadAvatar: async (file: File): Promise<{ success: boolean; avatarUrl?: string; profile?: Profile; error?: string }> => {
        try {
            // Validate file size (5MB)
            if (file.size > 5 * 1024 * 1024) {
                return { success: false, error: i18n.t('settings.profileTab.avatarUploadError') };
            }

            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                return { success: false, error: i18n.t('settings.profileTab.avatarUploadError') };
            }

            const headers = await getAuthHeader();
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}/profile/avatar`, {
                method: 'POST',
                headers,
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                return {
                    success: false,
                    error: errorData.message || i18n.t('settings.profileTab.avatarUploadError')
                };
            }

            const data = await response.json();
            return {
                success: true,
                avatarUrl: data.avatarUrl,
                profile: data.profile
            };
        } catch (error: any) {
            console.error('Error uploading avatar:', error);
            return {
                success: false,
                error: error.message || i18n.t('settings.profileTab.avatarUploadErrorGeneric')
            };
        }
    },

    /**
     * Delete avatar image
     * @returns Success status
     */
    deleteAvatar: async (): Promise<{ success: boolean; error?: string }> => {
        try {
            const headers = await getAuthHeader();
            const response = await fetch(`${API_URL}/profile/avatar`, {
                method: 'DELETE',
                headers
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                return {
                    success: false,
                    error: errorData.message || i18n.t('settings.profileTab.avatarDeleteError')
                };
            }

            return { success: true };
        } catch (error: any) {
            console.error('Error deleting avatar:', error);
            return {
                success: false,
                error: error.message || i18n.t('settings.profileTab.avatarDeleteErrorGeneric')
            };
        }
    },

    /**
     * Initialize user profile (called after OAuth login)
     */
    initProfile: async (): Promise<{ success: boolean; error?: string }> => {
        try {
            const headers = await getAuthHeader();
            const response = await fetch(`${API_URL}/profile/init`, {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                return { success: false, error: 'Failed to initialize profile' };
            }

            return { success: true };
        } catch (error: any) {
            console.error('Error initializing profile:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Update current user's profile
     */
    updateProfile: async (data: { firstName?: string; lastName?: string; height?: number; gender?: 'male' | 'female' | 'other' }): Promise<{ success: boolean; user?: any; error?: string }> => {
        try {
            const headers = await getAuthHeader();
            const response = await fetch(`${API_URL}/profile`, {
                method: 'PUT',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                return {
                    success: false,
                    error: errorData.message || i18n.t('settings.profileTab.profileUpdateError')
                };
            }

            const result = await response.json();
            return {
                success: true,
                user: result.user
            };
        } catch (error: any) {
            console.error('Error updating profile:', error);
            return {
                success: false,
                error: error.message || i18n.t('settings.profileTab.profileUpdateErrorGeneric')
            };
        }
    }
};
